import { PlanNoteOrientation } from "building-types/casework/caseworkTypesEnum";
import type { RoomHandle } from "building-types/handles/docEntityHandleTypes";
import { PlanOrientation } from "building-types/settings/settingsTypesEnum";
import type { SheetGeometry, SheetPolylineGeometry } from "building-types/views/sheetGeometryTypes";
import {
  SheetGeometryType,
  sheetPolylineGeometryNew,
  sheetTextGeometryNew,
} from "building-types/views/sheetGeometryTypes";
import colors from "common/constants/colorsOld";
import { percentNew } from "common/utils/nominalNumbers";
import { PlacementXAnchor, PlacementYAnchor } from "common/utils/textAlignment";
import { WALL_CUT_STROKE_WIDTH } from "doc/plans/constants";
import type { PlanCollider } from "doc/plans/interiorLabelObstacle";
import { CollisionType, planColliderObstacleNew } from "doc/plans/interiorLabelObstacle";
import type { Obstacles, PlanGenerationContext } from "doc/plans/planGeometrySource";
import type { TaggedPlanGeometry } from "doc/plans/planGeometrySourceTypes";
import { makePlanColliderFromSheetGeometry } from "doc/plans/sheetGeometryCollision";
import { aabb2ByCenterWidthHeight } from "geom/aabb2";
import { HALF_PI } from "geom/core";
import type { PolyLine2 } from "geom/polyLine2Types";
import { extractLevelCaseworkPlanInfo } from "lib/house/plans/casework";
import { CASEWORK_FONT_SIZE } from "pdf/planFonts";

export function makeSheetGeometryFromCasework(props: PlanGenerationContext) {
  return [
    ...makeSheetGeometryFromCaseworkSolid(props),
    ...makeSheetGeometryFromCaseworkDashed(props),
    ...makeSheetGeometryFromCaseworkPoche(props),
  ];
}

export function makeSheetGeometryFromCaseworkSolidDashed(props: PlanGenerationContext) {
  return [...makeSheetGeometryFromCaseworkSolid(props), ...makeSheetGeometryFromCaseworkDashed(props)];
}

function makeSheetGeometryFromCaseworkSolid({ building, levelId }: PlanGenerationContext) {
  const levelCaseworkInfo = extractLevelCaseworkPlanInfo(building, levelId);

  const caseworkSolidLines: SheetPolylineGeometry[] = [];

  levelCaseworkInfo.forEach((x) => {
    const { caseworkId, solidLines, roomId } = x;
    const handle: RoomHandle = { type: "ROOM", id: roomId };
    const buildingEntity = building.casework[caseworkId];
    if (solidLines.length > 0) {
      caseworkSolidLines.push(
        sheetPolylineGeometryNew({
          handle,
          paths: solidLines,
          fill: false,
          dash: false,
          buildingEntity,
        }),
      );
    }
  });

  return caseworkSolidLines;
}

function makeSheetGeometryFromCaseworkDashed({ building, levelId }: PlanGenerationContext) {
  const levelCaseworkInfo = extractLevelCaseworkPlanInfo(building, levelId);

  const caseworkDashLines: SheetPolylineGeometry[] = [];

  levelCaseworkInfo.forEach((x) => {
    const { caseworkId, dashLines, roomId } = x;
    const handle: RoomHandle = { type: "ROOM", id: roomId };
    const buildingEntity = building.casework[caseworkId];
    if (dashLines.length > 0) {
      caseworkDashLines.push(
        sheetPolylineGeometryNew({
          handle,
          paths: dashLines,
          fill: false,
          dash: true,
          buildingEntity,
        }),
      );
    }
  });

  return caseworkDashLines;
}

export function makeSheetGeometryFromCaseworkPoche({ building, levelId }: PlanGenerationContext) {
  const levelCaseworkInfo = extractLevelCaseworkPlanInfo(building, levelId);

  const caseworkPocheLines: SheetPolylineGeometry[] = [];

  levelCaseworkInfo.forEach((x) => {
    const { caseworkId, pocheLines, roomId } = x;
    const handle: RoomHandle = { type: "ROOM", id: roomId };
    const buildingEntity = building.casework[caseworkId];
    if (pocheLines.length > 0) {
      caseworkPocheLines.push(
        sheetPolylineGeometryNew({
          handle,
          paths: pocheLines,
          linewidth: WALL_CUT_STROKE_WIDTH,
          fill: true,
          fillColor: colors.stoneLight,
          fillOpacity: percentNew(1),
          dash: false,
          buildingEntity,
        }),
      );
    }
  });

  return caseworkPocheLines;
}

export function makeSheetGeometryFromCaseworkNotesAndTags({ building, levelId, noSettings }: PlanGenerationContext) {
  const result: SheetGeometry[] = [];

  const levelCaseworkInfo = extractLevelCaseworkPlanInfo(building, levelId);

  levelCaseworkInfo.forEach((info) => {
    info.notes.forEach((n) => {
      let rotationRads = 0;
      if (n.orientation === PlanNoteOrientation.VERTICAL) {
        rotationRads = HALF_PI;
        if (building.settings.planset.orientation === PlanOrientation.ROTATE_90_DEGREES) {
          rotationRads *= -1;
        }
      }

      result.push(
        sheetTextGeometryNew({
          handle: building.levels[levelId].handle,
          text: n.note,
          x: n.position[0],
          y: n.position[1],
          rotation: rotationRads,
          fontSize: noSettings ? undefined : CASEWORK_FONT_SIZE,
          relaxationRangeAABB: aabb2ByCenterWidthHeight(n.position[0], n.position[1], 1, 1),
        }),
      );
    });
    if (info.tag && info.tagPlacements) {
      for (let i = 0; i < info.tagPlacements.length; i++) {
        const {
          position: [x, y],
          normal: [nx, ny],
        } = info.tagPlacements[i];

        // get rotation from normal
        const rotationRads = Math.atan2(ny, nx) - HALF_PI;

        result.push(
          sheetTextGeometryNew({
            handle: building.levels[levelId].handle,
            text: info.tag.text,
            x,
            y,
            rotation: rotationRads,
            fontSize: noSettings ? undefined : CASEWORK_FONT_SIZE,
            relaxationRangeAABB: aabb2ByCenterWidthHeight(x, y, 1, 1),
            placementXAnchor: PlacementXAnchor.CENTER,
            placementYAnchor: PlacementYAnchor.CENTER,
          }),
        );
      }
    }
  });

  return result;
}

export function collectCaseworkObstacles(o: Obstacles, geom: SheetGeometry, pred: (geom: PolyLine2) => boolean) {
  if (geom.type === SheetGeometryType.POLYLINE) {
    geom.paths.forEach((pl) => pred(pl) && o.obstacles?.push(planColliderObstacleNew(CollisionType.Casework, pl)));
  }
}

// TODO: don't relax these in floor plan (previously restricted by tiny rangeAABB)
export function caseworkLabelPlanColliders(labels: readonly TaggedPlanGeometry[]): (PlanCollider | undefined)[] {
  return labels.map((cl) => {
    if (cl.type === SheetGeometryType.TEXT) {
      return makePlanColliderFromSheetGeometry({
        collisionType: CollisionType.CaseWorkNote,
        sheetTextGeomSource: cl,
        labelBorder: 0,
      });
    }
    return undefined;
  });
}
